import { useState } from 'react';
import { formatDate } from 'utils/newsroom';
import { faqSponsorStatusLabel } from 'utils/faq-sponsor';
import FaqReview from './FaqReview';
import styles from 'styles/modules/newsAlert/Newsroom.module.scss';

// Evergreen Sponsor: the "Expert answers" slot under the story, offered to the
// alert's responders by email once the story is live.
const FaqOfferFields = ({
    alert,
    researchOn,
    story,
    draft,
    onChange,
    onStoryChange,
}) => {
    const sponsored = Boolean(alert.isSponsored);
    // The story research sponsorship sells this slot too, so the API refuses the offer.
    const blocked = sponsored || researchOn;
    const sentAt = story?.faqOfferSentAt;
    const status = faqSponsorStatusLabel(story);
    // Answers are written to these questions, matched by position.
    const locked = Boolean(story?.faqSponsorStatus);
    // Kept here: approving or declining changes what the review panel shows.
    const [reviewNotice, setReviewNotice] = useState(null);

    const setQuestion = (index, value) =>
        onChange(
            'faqQuestions',
            draft.faqQuestions.map((q, i) => (i === index ? value : q))
        );

    return (
        <fieldset className={styles.faqOffer}>
            <legend className={styles.kicker}>Evergreen Sponsor</legend>

            <label className={styles.checkboxLabel}>
                <input
                    type="checkbox"
                    checked={draft.faqOffer}
                    disabled={blocked}
                    onChange={(e) => onChange('faqOffer', e.target.checked)}
                />
                Offer FAQ sponsorship to responders
            </label>
            {sponsored ? (
                <p className={styles.hint}>
                    Alert is sponsored; the sponsor&rsquo;s answers run here.
                </p>
            ) : researchOn ? (
                <p className={styles.hint}>
                    Not available: this alert offers a story research
                    sponsorship, which includes the expert answers. Turn it off
                    in Edit first.
                </p>
            ) : (
                <p className={styles.hint}>
                    {sentAt
                        ? `Offer emailed to the responders on ${formatDate(
                              sentAt
                          )}. It is sent only once.`
                        : 'Emailed once to every expert who responded, when the story is live. First come, first served. Untick to publish without the offer.'}
                </p>
            )}
            {status && <p className={styles.faqStatus}>{status}</p>}
            {story?.faqSponsorStatus && (
                <FaqReview
                    alertId={alert.id}
                    story={story}
                    onStoryChange={onStoryChange}
                    onNotice={setReviewNotice}
                />
            )}
            {reviewNotice && (
                <p
                    className={
                        reviewNotice.type === 'error' ? styles.error : styles.ok
                    }
                    role={reviewNotice.type === 'error' ? 'alert' : 'status'}
                >
                    {reviewNotice.text}
                </p>
            )}
            {locked && (
                <p className={styles.hint}>
                    The heading and questions are fixed now that the slot is
                    sold.
                </p>
            )}

            {!sponsored && draft.faqOffer && (
                <>
                    <label className={styles.label} htmlFor="faq-heading">
                        FAQ heading
                    </label>
                    <input
                        id="faq-heading"
                        className={styles.input}
                        value={draft.faqHeading}
                        maxLength={255}
                        disabled={locked}
                        placeholder="Financial advice for women going through divorce: your questions answered"
                        onChange={(e) => onChange('faqHeading', e.target.value)}
                    />
                    <span className={styles.label}>
                        Questions (at least one)
                    </span>
                    {draft.faqQuestions.map((question, i) => (
                        <input
                            key={i}
                            aria-label={`Question ${i + 1}`}
                            className={[styles.input, styles.faqQuestion].join(
                                ' '
                            )}
                            value={question}
                            maxLength={255}
                            disabled={locked}
                            placeholder={`Question ${i + 1}`}
                            onChange={(e) => setQuestion(i, e.target.value)}
                        />
                    ))}
                </>
            )}
        </fieldset>
    );
};

export default FaqOfferFields;
